import { StringContext } from "../../../core/StringContext";
import { StringDevModule, StringDevOverlayConfig } from "./StringDevModule";
import { StringObject } from "../../../objects/StringObject";
import { styleTxn } from "../../../utils/style-txn";
import {
  AnchorPoint,
  overlaySlotRegistry,
  STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
  StringDevtoolsOverlayId,
} from "./StringDevtoolsOverlayLayout";
import { StringDevViewportLayer } from "./StringDevViewportLayer";

const RULERS_LEFT_PANEL_WIDTH = 20;
const RULERS_TOP_PANEL_HEIGHT = 20;
const BADGE_RULER_OFFSET_X = 24;
const BADGE_RULER_OFFSET_Y = 24;
const RULERS_HIDDEN_ATTRIBUTE = "data-string-rulers-hidden";
const RULERS_LEFT_SELECTOR = "[data-string-rulers-left]";
const RULERS_TOP_SELECTOR = "[data-string-rulers-top]";

export type OverlayAxis = "y" | "x";
export type OverlayAnchor = "start" | "end";

export interface OverlayBaseMetrics {
  visible: boolean;
  contentX: number;
  contentY: number;
  width: number;
  height: number;
  isOffscreen: boolean;
  baseAnchorX: number;
  baseAnchorY: number;
  collisionOffset: number;
  axis: OverlayAxis;
  primarySize: number;
  crossSize: number;
  primaryContentOffset: number;
  viewportPrimarySize: number;
}

interface OverlayObjectGeometry {
  contentX: number;
  contentY: number;
  width: number;
  height: number;
}

const VIEWPORT_ANCHOR_MAP: Record<string, OverlayAnchor> = {
  top: "start",
  left: "start",
  start: "start",
  bottom: "end",
  right: "end",
  end: "end",
};

export function resolveOverlayAnchor(raw: string | null | undefined): OverlayAnchor {
  if (!raw) return "start";
  return VIEWPORT_ANCHOR_MAP[raw.toLowerCase()] ?? "start";
}

export interface StringDevOverlayBadgeInit {
  targetId: string;
  selectorAttribute?: string;
  depth?: number;
  attributes?: Record<string, string | boolean | number | null | undefined>;
}

export interface StringDevOverlayBadgeState {
  visible?: boolean;
  active?: boolean;
  disabled?: boolean;
  title?: string;
  html?: string;
  label?: string;
  attributes?: Record<string, string | boolean | number | null | undefined>;
}

export abstract class StringDevOverlayModule<
  TEntry = unknown,
  TMeasurement = unknown,
> extends StringDevModule {
  private _viewportLayer?: StringDevViewportLayer;
  private _badgeLayer?: StringDevViewportLayer;
  private _hudLayer?: StringDevViewportLayer;

  protected get viewportLayer(): StringDevViewportLayer {
    if (!this._viewportLayer) {
      this._viewportLayer = this.acquireViewportLayer(this.layerName, this.layerZIndex);
    }
    return this._viewportLayer;
  }

  protected get badgeLayer(): StringDevViewportLayer {
    if (!this._badgeLayer) {
      this._badgeLayer = this.acquireViewportLayer("devtools-badges", 10030);
    }
    return this._badgeLayer;
  }

  protected get hudLayer(): StringDevViewportLayer {
    if (!this._hudLayer) {
      this._hudLayer = this.acquireViewportLayer("devtools-hud", 10040);
    }
    return this._hudLayer;
  }
  protected readonly entries = new Map<string, TEntry>();
  protected readonly measurements = new Map<string, TMeasurement>();

  protected enabled = false;
  protected needsMeasure = false;
  protected hasPendingMutate = false;

  private _rafId: number | null = null;
  private _pendingSingleIds = new Set<string>();

  private readonly onOverlayLayoutChangeBind = () => {
    if (!this.enabled) return;
    this.scheduleSync();
  };

  private get _overlayConfig(): StringDevOverlayConfig | undefined {
    return (this.constructor as typeof StringDevModule).devtool?.overlay;
  }

  /** Override to provide the overlay ID for the anchor/collision system. */
  protected get overlayId(): StringDevtoolsOverlayId | null {
    return this._overlayConfig?.overlayId ?? null;
  }

  /** Override to declare how many badge slots this overlay occupies. */
  protected get overlayBadgeCount(): number {
    return 1;
  }

  /** Override to define your viewport layer name. Falls back to devtool.overlay.layerName. */
  protected get layerName(): string {
    return this._overlayConfig?.layerName ?? "";
  }

  /** Override to define your viewport layer z-index. Falls back to devtool.overlay.zIndex. */
  protected get layerZIndex(): number {
    return this._overlayConfig?.zIndex ?? 10011;
  }

  /** Override to define the data-attribute added to the layer root. Falls back to devtool.overlay.layerAttribute. */
  protected get layerAttribute(): string {
    return this._overlayConfig?.layerAttribute ?? "";
  }

  /** Override when an overlay module should start enabled before dock preferences apply. Falls back to devtool.overlay.defaultEnabled. */
  protected get defaultEnabled(): boolean {
    return this._overlayConfig?.defaultEnabled ?? false;
  }

  constructor(context: StringContext) {
    super(context);
    this.enabled = this.defaultEnabled;
  }

  onInit(): void {
    this.ensureLayer();
    this.applyEnabledState();
    window.addEventListener(
      STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
      this.onOverlayLayoutChangeBind,
    );
  }

  onObjectConnected(object: StringObject): void {
    this.ensureLayer();
    const entry = this.createOverlayEntry(object);
    if (!entry) return;

    this.entries.set(object.id, entry);
    this.syncSlotRegistration();
    this.scheduleSync();
  }

  onObjectDisconnected(object: StringObject): void {
    const entry = this.entries.get(object.id);
    if (!entry) return;

    this.destroyOverlayEntry(entry);
    this.entries.delete(object.id);
    this.measurements.delete(object.id);
    this.syncSlotRegistration();
  }

  public setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      this.measurements.clear();
    }

    this.applyEnabledState();
    this.onEnabledChange(enabled);
    this.scheduleFullSync();
    this.emitDevtoolState();
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  protected getDevtoolActiveState(): boolean {
    return this.enabled;
  }

  protected setDevtoolActiveState(active: boolean): void {
    this.setEnabled(active);
  }

  // --- Abstract Methods ---

  /** Create DOM and return state for the given element */
  protected abstract createOverlayEntry(object: StringObject): TEntry | null;

  /** Clean up DOM and listeners for the given element */
  protected abstract destroyOverlayEntry(entry: TEntry): void;

  /** Produce a custom measurement state utilizing the provided base metrics */
  protected abstract measureEntry(entry: TEntry, baseMetrics: OverlayBaseMetrics): TMeasurement;

  /** Render DOM efficiently utilizing `styleTxn` */
  protected abstract applyMeasurement(entry: TEntry, measurement: TMeasurement | undefined): void;

  /** Called when `setEnabled` state flips, allowing subclasses to show/hide specific entries */
  protected onEnabledChange(_enabled: boolean): void {}

  // --- Render Loop (Measurement and Syncing) ---

  onScroll(): void {
    if (!this.enabled) return;
    this.needsMeasure = true;
  }

  onScrollMeasure(): void {
    if (!this.enabled || !this.needsMeasure) return;
    this.collectMeasurements();
    this.needsMeasure = false;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  onMutate(): void {
    if (!this.enabled || !this.hasPendingMutate) return;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  onResize(): void {
    if (!this.enabled) return;
    this.scheduleSync();
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.scheduleSync();
  }

  // --- Internal implementations ---

  /**
   * Immediate synchronous full sync — use only when instant visual response
   * is required (e.g. setEnabled). For everything else prefer scheduleSync().
   */
  protected scheduleFullSync(): void {
    this.ensureLayer();
    this._pendingSingleIds.clear();
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.needsMeasure = true;
    this.collectMeasurements();
    this.needsMeasure = false;
    this.flushMeasurements();
    this.hasPendingMutate = false;
  }

  /**
   * Deferred full sync — batches multiple calls within the same animation
   * frame into a single measure+flush pass. Use for resize, layout changes,
   * object connected, and any other non-immediate triggers.
   */
  protected scheduleSync(): void {
    if (!this.enabled) return;
    this._pendingSingleIds.clear();
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (!this.enabled) return;
      this.ensureLayer();
      this.collectMeasurements();
      this.flushMeasurements();
    });
  }

  /**
   * Deferred single-entry sync — measures and flushes only the given entry.
   * Multiple calls for different entries within the same frame are batched
   * together into one styleTxn flush. Use for per-object events like
   * object:inview or slider input where only one entry changes.
   */
  protected scheduleSingleSync(entry: TEntry): void {
    if (!this.enabled) return;
    const id = (entry as any).object?.id ?? (entry as any).targetId;
    if (!id) {
      this.scheduleSync();
      return;
    }
    this._pendingSingleIds.add(id);
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      if (!this.enabled) return;
      this.ensureLayer();
      const ids = this._pendingSingleIds;
      this._pendingSingleIds = new Set();
      const measured: TEntry[] = [];
      for (const entryId of ids) {
        const e = this.entries.get(entryId);
        if (!e) continue;
        const bMetrics = this.computeBaseMetrics(e);
        const measurement = this.measureEntry(e, bMetrics);
        this.measurements.set(entryId, measurement);
        measured.push(e);
      }
      styleTxn.run(() => {
        for (const e of measured) {
          const entryId = (e as any).object?.id ?? (e as any).targetId;
          if (!entryId) continue;
          this.applyMeasurement(e, this.measurements.get(entryId));
        }
      });
    });
  }

  protected collectMeasurements(): void {
    this.ensureLayer();
    for (const [id, entry] of this.entries) {
      const bMetrics = this.computeBaseMetrics(entry);
      this.measurements.set(id, this.measureEntry(entry, bMetrics));
    }
  }

  protected flushMeasurements(): void {
    styleTxn.run(() => {
      for (const [id, entry] of this.entries) {
        this.applyMeasurement(entry, this.measurements.get(id));
      }
    });
  }

  protected ensureLayer(): HTMLDivElement {
    const root = this.viewportLayer.ensure(this.getWorldHost());
    if (this.layerAttribute) {
      root.setAttribute(this.layerAttribute, "");
    }
    return root;
  }

  protected applyEnabledState(): void {
    const layer = this.viewportLayer.getElement();
    if (!layer) return;

    layer.setAttribute("data-enabled", this.enabled ? "true" : "false");

    if (this.layerAttribute) {
      if (this.enabled) {
        layer.setAttribute(this.layerAttribute, "");
      } else {
        layer.removeAttribute(this.layerAttribute);
      }
    }

    this.syncSlotRegistration();
  }

  private syncSlotRegistration(): void {
    if (!this.overlayId) return;

    const shouldOccupySlot = this.enabled && this.entries.size > 0;
    if (shouldOccupySlot) {
      overlaySlotRegistry.register(
        this.overlayId,
        this.devtoolConfig?.order ?? 0,
        this.overlayBadgeCount,
      );
    } else {
      overlaySlotRegistry.unregister(this.overlayId);
    }

    overlaySlotRegistry.emitLayoutChange();
  }

  protected resolveOverlayTargetId(object: StringObject): string {
    return object.htmlElement.id || object.id;
  }

  protected resolveOverlayObjectDepth(element: HTMLElement): number {
    let depth = 0;
    let current = element.parentElement;

    while (current) {
      if (current.hasAttribute("string-id") || current.hasAttribute("data-string-id")) {
        depth += 1;
      }
      current = current.parentElement;
    }

    return depth;
  }

  protected createOverlayBadge(init: StringDevOverlayBadgeInit): HTMLButtonElement {
    const badge = document.createElement("button");
    badge.type = "button";

    badge.setAttribute("data-stdg-button", "");
    badge.setAttribute("data-stdg-button-icon-16", "");
    badge.setAttribute("data-string-devtools-overlay-badge", "");
    badge.setAttribute("data-target-id", init.targetId);

    if (init.selectorAttribute) {
      badge.setAttribute(init.selectorAttribute, "");
    }

    if (init.depth != null) {
      badge.setAttribute("data-depth", String(init.depth));
    }

    this.applyOverlayBadgeState(badge, { attributes: init.attributes });
    return badge;
  }

  protected applyOverlayBadgeState(badge: HTMLElement, state: StringDevOverlayBadgeState): void {
    if (state.visible != null) {
      badge.setAttribute("data-visible", state.visible ? "true" : "false");
    }

    if (state.active != null) {
      badge.setAttribute("data-active", state.active ? "true" : "false");
    }

    if (state.disabled != null && badge instanceof HTMLButtonElement) {
      badge.disabled = state.disabled;
    }

    if (state.title != null) {
      badge.title = state.title;
    }

    if (state.html != null) {
      const existingLabel = badge.querySelector<HTMLElement>("[data-stdg-badge-label]");
      badge.innerHTML = state.html;
      if (existingLabel) badge.appendChild(existingLabel);
    }

    if (state.label !== undefined) {
      let labelEl = badge.querySelector<HTMLElement>("[data-stdg-badge-label]");
      if (state.label === null || state.label === "") {
        labelEl?.remove();
      } else {
        if (!labelEl) {
          labelEl = document.createElement("span");
          labelEl.setAttribute("data-stdg-badge-label", "");
          badge.appendChild(labelEl);
        }
        if (labelEl.textContent !== state.label) {
          labelEl.textContent = state.label;
        }
      }
    }

    if (!state.attributes) return;

    for (const [key, value] of Object.entries(state.attributes)) {
      if (value == null) {
        badge.removeAttribute(key);
        continue;
      }

      if (typeof value === "boolean") {
        badge.setAttribute(key, value ? "true" : "false");
        continue;
      }

      badge.setAttribute(key, String(value));
    }
  }

  protected applyOverlayBadgePosition(
    badge: HTMLElement,
    measurement: { visible: boolean; docX: number; docY: number; translate?: string } | undefined,
  ): void {
    if (!measurement) {
      badge.setAttribute("data-visible", "false");
      return;
    }

    badge.setAttribute("data-visible", measurement.visible ? "true" : "false");
    if (!measurement.visible) {
      return;
    }

    styleTxn.setProps(badge, {
      transform: `translate3d(${Math.round(measurement.docX)}px, ${Math.round(measurement.docY)}px, 0)${measurement.translate ? ` ${measurement.translate}` : ""}`,
    });
  }

  protected getOverlayTargetLabel(object: StringObject): string {
    const element = object.htmlElement;
    const elementId = element.getAttribute("id")?.trim();
    if (elementId) return `#${elementId}`;

    const className = element.getAttribute("class")?.trim();
    if (className) {
      const classSelector = className
        .split(/\s+/)
        .filter(Boolean)
        .map((name) => `.${name}`)
        .join("");
      if (classSelector) return classSelector;
    }

    const stringId =
      element.getAttribute("string-id")?.trim() || element.getAttribute("data-string-id")?.trim();
    if (stringId) return stringId;

    return object.id || "<unnamed>";
  }

  protected getViewportAnchorOffset(anchor: OverlayAnchor, metrics: OverlayBaseMetrics): number {
    return anchor === "end" ? metrics.viewportPrimarySize : 0;
  }

  protected getOverlayAnchorAdjustment(
    _object: StringObject,
    _anchor: AnchorPoint,
    _metrics?: { contentX: number; contentY: number; width: number; height: number },
  ): AnchorPoint {
    return { docX: 0, docY: 0 };
  }

  protected resolveRulerPanelOffset(contentX: number, contentY: number): { x: number; y: number } {
    if (typeof document === "undefined") return { x: 0, y: 0 };
    if (document.body.hasAttribute(RULERS_HIDDEN_ATTRIBUTE)) return { x: 0, y: 0 };
    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();
    const x =
      !!document.querySelector(RULERS_LEFT_SELECTOR) &&
      contentX - scrollLeft < RULERS_LEFT_PANEL_WIDTH
        ? BADGE_RULER_OFFSET_X
        : 0;
    const y =
      !!document.querySelector(RULERS_TOP_SELECTOR) &&
      contentY - scrollTop < RULERS_TOP_PANEL_HEIGHT
        ? BADGE_RULER_OFFSET_Y
        : 0;
    return { x, y };
  }

  protected computeBaseMetrics(entry: any): OverlayBaseMetrics {
    const object = entry.object as StringObject;
    const element = object.htmlElement;
    if (!this.enabled || !element || !element.isConnected) {
      return this.getHiddenMetrics();
    }

    const geometry = this.resolveOverlayObjectGeometry(object, element);
    const { contentX, contentY, width, height } = geometry;

    if (width <= 0 || height <= 0) {
      return this.getHiddenMetrics();
    }

    const viewportHeight = this.data.viewport.windowHeight || window.innerHeight;
    const viewportWidth = this.data.viewport.windowWidth || window.innerWidth;
    const scrollLeft = this.getViewportScrollLeft();
    const scrollTop = this.getViewportScrollTop();

    const viewportLeft = contentX - scrollLeft;
    const viewportTop = contentY - scrollTop;

    const isOffscreen =
      viewportTop + height < 0 ||
      viewportTop > viewportHeight ||
      viewportLeft + width < 0 ||
      viewportLeft > viewportWidth;

    if (isOffscreen) {
      return this.getHiddenMetrics();
    }

    let baseAnchorX = contentX;
    let baseAnchorY = contentY;
    let collisionOffset = 0;

    if (this.overlayId) {
      const effectiveAnchorCache = new Map<string, { base: AnchorPoint; effective: AnchorPoint } | null>();

      const resolveEffectiveAnchor = (
        targetObject: StringObject,
      ): { base: AnchorPoint; effective: AnchorPoint } | null => {
        if (!this.overlayId) return null;
        const targetEntry = this.entries.get(targetObject.id);
        if (!targetEntry) {
          effectiveAnchorCache.set(targetObject.id, null);
          return null;
        }

        const existingMeasurement = this.measurements.get(targetObject.id) as
          | { visible?: boolean }
          | undefined;
        if (existingMeasurement?.visible === false) {
          effectiveAnchorCache.set(targetObject.id, null);
          return null;
        }

        const cached = effectiveAnchorCache.get(targetObject.id);
        if (cached !== undefined) {
          return cached;
        }

        const targetElement = targetObject.htmlElement;
        if (!targetElement) {
          effectiveAnchorCache.set(targetObject.id, null);
          return null;
        }

        const targetGeometry = this.resolveOverlayObjectGeometry(targetObject, targetElement);
        const targetContentX = targetGeometry.contentX;
        const targetContentY = targetGeometry.contentY;
        const targetWidth = targetGeometry.width;
        const targetHeight = targetGeometry.height;

        const targetAnchor = overlaySlotRegistry.resolveAnchor(
          this.overlayId,
          targetContentX,
          targetContentY,
        );
        const targetAdjustment = this.getOverlayAnchorAdjustment(targetObject, targetAnchor, {
          contentX: targetContentX,
          contentY: targetContentY,
          width: targetWidth,
          height: targetHeight,
        });
        const targetRulerOffset = this.resolveRulerPanelOffset(targetContentX, targetContentY);
        const targetBaseX = targetAnchor.docX + targetAdjustment.docX + targetRulerOffset.x;
        const targetBaseY = targetAnchor.docY + targetAdjustment.docY + targetRulerOffset.y;
        const targetCollisionOffset = overlaySlotRegistry.resolveCollisionOffset(
          targetElement,
          targetBaseX,
          targetBaseY,
          (ancestorId) => {
            const ancestorObject = this.objectManager.all.get(ancestorId);
            if (!ancestorObject) return null;
            const effective = resolveEffectiveAnchor(ancestorObject)?.effective;
            if (effective) return effective;
            // Ancestor exists but has no badge for this overlay — fall back to its element Y
            // so all overlays on the same child get consistent collision offsets.
            // Use targetBaseX so the X check is a no-op and only Y proximity matters.
            return {
              docX: targetBaseX,
              docY: this.resolveOverlayObjectGeometry(ancestorObject).contentY,
            };
          },
        );
        const resolvedAnchor = {
          base: {
            docX: targetBaseX,
            docY: targetBaseY,
          },
          effective: {
            docX: targetBaseX,
            docY: targetBaseY + targetCollisionOffset,
          },
        };
        effectiveAnchorCache.set(targetObject.id, resolvedAnchor);
        return resolvedAnchor;
      };

      const resolvedAnchor = resolveEffectiveAnchor(object);
      if (resolvedAnchor) {
        baseAnchorX = resolvedAnchor.base.docX;
        baseAnchorY = resolvedAnchor.base.docY;
        collisionOffset = resolvedAnchor.effective.docY - resolvedAnchor.base.docY;
      }
    }

    const axis: OverlayAxis = this.data.scroll.direction === "horizontal" ? "x" : "y";
    const primarySize = axis === "y" ? height : width;
    const crossSize = axis === "y" ? width : height;
    const primaryContentOffset = axis === "y" ? contentY : contentX;
    const viewportPrimarySize = axis === "y" ? viewportHeight : viewportWidth;

    return {
      visible: true,
      contentX,
      contentY,
      width,
      height,
      isOffscreen,
      baseAnchorX,
      baseAnchorY,
      collisionOffset,
      axis,
      primarySize,
      crossSize,
      primaryContentOffset,
      viewportPrimarySize,
    };
  }

  protected getHiddenMetrics(): OverlayBaseMetrics {
    const axis: OverlayAxis = this.data.scroll.direction === "horizontal" ? "x" : "y";
    return {
      visible: false,
      contentX: 0,
      contentY: 0,
      width: 0,
      height: 0,
      isOffscreen: true,
      baseAnchorX: 0,
      baseAnchorY: 0,
      collisionOffset: 0,
      axis,
      primarySize: 0,
      crossSize: 0,
      primaryContentOffset: 0,
      viewportPrimarySize: 0,
    };
  }

  protected getObjectDocY(object: StringObject): number {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");
    if (start != null && size != null && size > 0) {
      return start;
    }
    return object.getProperty<number>("layout-doc-top") ?? 0;
  }

  protected resolveOverlayObjectGeometry(
    object: StringObject,
    element: HTMLElement = object.htmlElement,
  ): OverlayObjectGeometry {
    const liveRect = element.getBoundingClientRect();
    const liveMetrics = this.resolveLiveElementMetrics(element, liveRect);
    const width =
      liveMetrics.width ||
      (object.getProperty<number>("layout-width") ??
        (object.getProperty<number>("half-width") ?? 0) * 2) ||
      element.offsetWidth ||
      element.clientWidth ||
      0;

    const measuredSize = object.getProperty<number>("size");
    const layoutHeight =
      liveMetrics.height ||
      (object.getProperty<number>("layout-height") ??
        (object.getProperty<number>("half-height") ?? 0) * 2) ||
      element.offsetHeight ||
      element.clientHeight ||
      0;

    return {
      contentX: liveMetrics.docLeft ?? object.getProperty<number>("layout-doc-left") ?? 0,
      contentY: liveMetrics.docTop ?? this.getObjectDocY(object),
      width,
      height: measuredSize != null && measuredSize > 0 ? measuredSize : layoutHeight,
    };
  }

  protected getViewportScrollLeft(): number {
    return this.data.scroll.elementContainer?.scrollLeft ?? 0;
  }

  protected getViewportScrollTop(): number {
    return this.data.scroll.elementContainer?.scrollTop ?? 0;
  }

  protected resolveLiveElementMetrics(
    element: HTMLElement,
    rect: DOMRect,
  ): { docLeft?: number; docTop?: number; width: number; height: number } {
    const width = rect.width || element.offsetWidth || element.clientWidth || 0;
    const height = rect.height || element.offsetHeight || element.clientHeight || 0;
    const host = this.getWorldHost();
    const scroller = this.data.scroll.elementContainer;

    if (!(width > 0) || !(height > 0)) {
      return { width, height };
    }

    if (host === document.body || host === document.documentElement) {
      return {
        docLeft: rect.left + (scroller?.scrollLeft ?? window.scrollX ?? 0),
        docTop: rect.top + (scroller?.scrollTop ?? window.scrollY ?? 0),
        width,
        height,
      };
    }

    const hostRect = host.getBoundingClientRect();
    return {
      docLeft: rect.left - hostRect.left + host.scrollLeft,
      docTop: rect.top - hostRect.top + host.scrollTop,
      width,
      height,
    };
  }

  destroy(): void {
    if (this.overlayId) {
      overlaySlotRegistry.unregister(this.overlayId);
    }
    window.removeEventListener(
      STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT,
      this.onOverlayLayoutChangeBind,
    );
    for (const entry of this.entries.values()) {
      this.destroyOverlayEntry(entry);
    }
    this.entries.clear();
    this.measurements.clear();
    super.destroy();
  }
}
